import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Award, Lock, Star, Trophy, Target, Users, BookOpen, TrendingUp } from "lucide-react";

const Badges = () => {
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    setLoading(true);
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/badges/my-badges`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAllBadges(allRes.data);
      setMyBadges(myRes.data.badges || []);
    } catch (error) {
      console.error("Error fetching badges:", error);
      toast.error("Failed to load badges");
    } finally {
      setLoading(false);
    }
  };

  const hasBadge = (badgeId) => {
    return myBadges.some(mb => mb.badge_id === badgeId);
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: "bg-gray-100 text-gray-800 border-gray-300",
      rare: "bg-blue-100 text-blue-800 border-blue-300",
      epic: "bg-purple-100 text-purple-800 border-purple-300",
      legendary: "bg-yellow-100 text-yellow-800 border-yellow-300"
    };
    return colors[rarity] || colors.common;
  };

  const filterBadgesByCategory = (category) => {
    if (category === "earned") {
      return allBadges.filter(badge => hasBadge(badge.id));
    }
    if (category === "locked") {
      return allBadges.filter(badge => !hasBadge(badge.id));
    }
    if (category === "all") {
      return allBadges;
    }
    return allBadges.filter(badge => badge.category === category);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Badges & Achievements</h1>
          <p className="text-gray-600">Earn badges by completing activities and reaching milestones</p>
          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-600" />
              <span className="font-semibold">{myBadges.length}</span>
              <span className="text-gray-600">Badges Earned</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-gray-400" />
              <span className="font-semibold">{allBadges.length - myBadges.length}</span>
              <span className="text-gray-600">Locked</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">All Badges</TabsTrigger>
            <TabsTrigger value="earned">Earned</TabsTrigger>
            <TabsTrigger value="locked">Locked</TabsTrigger>
            <TabsTrigger value="assessment">Assessment</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="learning">Learning</TabsTrigger>
            <TabsTrigger value="achievement">Achievement</TabsTrigger>
          </TabsList>

          {["all", "earned", "locked", "assessment", "social", "learning", "achievement"].map(category => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filterBadgesByCategory(category).map((badge) => {
                  const earned = hasBadge(badge.id);
                  const earnedBadge = myBadges.find(mb => mb.badge_id === badge.id);
                  
                  return (
                    <Card 
                      key={badge.id} 
                      className={`relative overflow-hidden transition-all hover:shadow-lg ${
                        earned ? 'border-2 border-blue-400' : 'opacity-75'
                      }`}
                    >
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                            earned ? `bg-blue-100` : 'bg-gray-100'
                          } ${!earned && 'grayscale'}`}>
                            <span className="text-4xl">{badge.icon}</span>
                          </div>

                          <h3 className={`font-bold text-lg mb-2 ${earned ? 'text-gray-900' : 'text-gray-600'}`}>
                            {badge.name}
                          </h3>

                          <Badge className={`mb-3 ${getRarityColor(badge.rarity)}`}>
                            {badge.rarity.charAt(0).toUpperCase() + badge.rarity.slice(1)}
                          </Badge>

                          <p className={`text-sm mb-3 ${earned ? 'text-gray-700' : 'text-gray-500'}`}>
                            {badge.description}
                          </p>

                          <div className="p-3 bg-gray-50 rounded-lg mb-3">
                            <p className="text-xs font-medium text-gray-600 mb-1">How to Earn:</p>
                            <p className="text-sm text-gray-800">{badge.requirement}</p>
                            {badge.points_required > 0 && (
                              <p className="text-xs text-gray-600 mt-1">
                                Requires {badge.points_required} points
                              </p>
                            )}
                          </div>

                          {earned ? (
                            <div className="flex items-center justify-center gap-2 text-green-600">
                              <Award className="w-4 h-4" />
                              <span className="text-sm font-medium">
                                Earned {earnedBadge ? new Date(earnedBadge.earned_at).toLocaleDateString() : ''}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                              <Lock className="w-4 h-4" />
                              <span className="text-sm font-medium">Locked</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {filterBadgesByCategory(category).length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">No badges in this category yet</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default Badges;